// A BOM or routing LAMP drafted from a released drawing, as an editable Form on its card (4.2.1.1, 4.2.2.1): every
// line names an Item on record and every operation a work centre on record, chosen from lists, so nothing LAMP
// guessed can slip in. What LAMP could not match is shown apart, as "LAMP could not match these", never folded into
// the lines. A new part is named here. Confirm sends the Form as it stands; LAMP checks it again before anything is
// written. Every control is a labelled native one, so the Form is filled in from the keyboard.
import { useState, type FormEvent, type ReactNode } from "react";
import type { BomFormPreview, CardForm, RoutingFormPreview } from "../api.js";
import { useWords } from "../i18n/words.js";

export interface SetUpFormProps {
  readonly preview: BomFormPreview | RoutingFormPreview;
  readonly canConfirm: boolean;
  readonly busy: boolean;
  readonly onConfirm: (form: CardForm) => void;
  /** What else the card offers beside Confirm (Send back). */
  readonly children?: ReactNode;
}

interface Line {
  readonly component: string;
  readonly quantity: string;
  readonly unit: string;
  readonly scrap_pct: string;
}

interface Operation {
  readonly sequence: string;
  readonly name: string;
  readonly work_centre: string;
  readonly job_worker: string;
  readonly setup_minutes: string;
  readonly cycle_minutes: string;
}

/** A number typed in a field, or undefined when it is not one. */
const numberIn = (value: string): number | undefined => {
  const n = Number(value.trim());
  return value.trim() === "" || !Number.isFinite(n) ? undefined : n;
};

function Unmatched({ unmatched }: { readonly unmatched: readonly string[] }) {
  const w = useWords();
  if (unmatched.length === 0) return null;
  return (
    <section className="alert unmatched" aria-label={w.setUpUnmatched}>
      <h4>{w.setUpUnmatched}</h4>
      <ul>
        {unmatched.map((u) => (
          <li key={u}>{u}</li>
        ))}
      </ul>
      <p className="muted">{w.setUpUnmatchedHelp}</p>
    </section>
  );
}

function BomForm({ preview, canConfirm, busy, onConfirm, children }: SetUpFormProps & { preview: BomFormPreview }) {
  const w = useWords();
  const [partName, setPartName] = useState("");
  const [partUnit, setPartUnit] = useState("piece");
  const [lines, setLines] = useState<readonly Line[]>(
    preview.lines.map((l) => ({
      component: l.component,
      quantity: String(l.quantity),
      unit: l.unit,
      scrap_pct: String(l.scrap_pct),
    })),
  );
  const unitsOf = (component: string) => preview.items.find((i) => i.id === component)?.units ?? [];
  const change = (at: number, changes: Partial<Line>) =>
    setLines(lines.map((line, i) => (i === at ? { ...line, ...changes } : line)));
  const ready =
    (preview.item !== null || partName.trim() !== "") &&
    lines.length > 0 &&
    lines.every((l) => l.component !== "" && l.unit !== "" && (numberIn(l.quantity) ?? 0) > 0);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    onConfirm({
      ...(preview.item === null ? { new_part_name: partName.trim(), new_part_unit: partUnit.trim() || "piece" } : {}),
      lines: lines.map((l) => ({
        component: l.component,
        quantity: numberIn(l.quantity) ?? 0,
        unit: l.unit,
        scrap_pct: Math.round(numberIn(l.scrap_pct) ?? 0),
      })),
    });
  };

  return (
    <form className="set-up" onSubmit={submit}>
      <p className="muted">{w.setUpFrom(preview.drawing, preview.revision)}</p>
      {preview.item ? (
        <dl className="facts">
          <dt>{w.setUpPart}</dt>
          <dd>{preview.item.name}</dd>
        </dl>
      ) : (
        <div className="row">
          <label className="field">
            {w.setUpNewPart}
            <input value={partName} required maxLength={200} onChange={(e) => setPartName(e.target.value)} />
          </label>
          <label className="field">
            {w.setUpPartUnit}
            <input value={partUnit} maxLength={20} onChange={(e) => setPartUnit(e.target.value)} />
          </label>
        </div>
      )}
      <table className="lines">
        <caption>{w.setUpMaterials}</caption>
        <thead>
          <tr>
            <th scope="col">{w.setUpItem}</th>
            <th scope="col">{w.setUpQuantity}</th>
            <th scope="col">{w.setUpUnit}</th>
            <th scope="col">{w.setUpScrap}</th>
            <td />
          </tr>
        </thead>
        <tbody>
          {lines.map((line, i) => {
            const n = i + 1;
            const units = unitsOf(line.component);
            return (
              <tr key={i}>
                <td>
                  <select
                    aria-label={`${w.setUpLine(n)}: ${w.setUpItem}`}
                    value={line.component}
                    onChange={(e) => {
                      const next = unitsOf(e.target.value);
                      change(i, {
                        component: e.target.value,
                        unit: next.includes(line.unit) ? line.unit : (next[0] ?? ""),
                      });
                    }}
                  >
                    <option value="">{w.setUpChoose}</option>
                    {preview.items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    aria-label={`${w.setUpLine(n)}: ${w.setUpQuantity}`}
                    inputMode="decimal"
                    value={line.quantity}
                    onChange={(e) => change(i, { quantity: e.target.value })}
                  />
                </td>
                <td>
                  <select
                    aria-label={`${w.setUpLine(n)}: ${w.setUpUnit}`}
                    value={line.unit}
                    onChange={(e) => change(i, { unit: e.target.value })}
                  >
                    {(units.includes(line.unit) || line.unit === "" ? units : [line.unit, ...units]).map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    aria-label={`${w.setUpLine(n)}: ${w.setUpScrap}`}
                    inputMode="numeric"
                    value={line.scrap_pct}
                    onChange={(e) => change(i, { scrap_pct: e.target.value })}
                  />
                </td>
                <td>
                  <button type="button" className="linkish" onClick={() => setLines(lines.filter((_, at) => at !== i))}>
                    {w.setUpRemoveLine(n)}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        type="button"
        className="btn"
        onClick={() => setLines([...lines, { component: "", quantity: "", unit: "", scrap_pct: "0" }])}
      >
        {w.setUpAddLine}
      </button>
      <Unmatched unmatched={preview.unmatched} />
      <div className="acts">
        {canConfirm ? (
          <button type="submit" className="btn btn-go" disabled={busy || !ready}>
            {w.cardConfirm}
          </button>
        ) : null}
        {children}
      </div>
    </form>
  );
}

function RoutingForm({
  preview,
  canConfirm,
  busy,
  onConfirm,
  children,
}: SetUpFormProps & { preview: RoutingFormPreview }) {
  const w = useWords();
  const [operations, setOperations] = useState<readonly Operation[]>(
    preview.operations.map((o) => ({
      sequence: String(o.sequence),
      name: o.name,
      work_centre: o.work_centre ?? "",
      job_worker: o.job_worker ?? "",
      setup_minutes: String(o.setup_minutes),
      cycle_minutes: String(o.cycle_minutes),
    })),
  );
  const change = (at: number, changes: Partial<Operation>) =>
    setOperations(operations.map((op, i) => (i === at ? { ...op, ...changes } : op)));
  const ready =
    operations.length > 0 &&
    operations.every(
      (o) => (numberIn(o.sequence) ?? 0) > 0 && o.name.trim() !== "" && (o.work_centre !== "" || o.job_worker !== ""),
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!ready) return;
    onConfirm({
      operations: operations.map((o) => ({
        sequence: Math.round(numberIn(o.sequence) ?? 0),
        name: o.name.trim(),
        ...(o.work_centre !== "" ? { work_centre: o.work_centre } : { job_worker: o.job_worker }),
        setup_minutes: numberIn(o.setup_minutes) ?? 0,
        cycle_minutes: numberIn(o.cycle_minutes) ?? 0,
      })),
    });
  };

  return (
    <form className="set-up" onSubmit={submit}>
      <p className="muted">{w.setUpFrom(preview.drawing, preview.revision)}</p>
      <table className="lines">
        <caption>{w.setUpOperations}</caption>
        <thead>
          <tr>
            <th scope="col">{w.setUpSequence}</th>
            <th scope="col">{w.setUpName}</th>
            <th scope="col">{w.setUpWorkCentre}</th>
            <th scope="col">{w.setUpSetupMinutes}</th>
            <th scope="col">{w.setUpCycleMinutes}</th>
            <td />
          </tr>
        </thead>
        <tbody>
          {operations.map((op, i) => {
            const n = i + 1;
            return (
              <tr key={i}>
                <td>
                  <input
                    aria-label={`${w.setUpOperation(n)}: ${w.setUpSequence}`}
                    inputMode="numeric"
                    value={op.sequence}
                    onChange={(e) => change(i, { sequence: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`${w.setUpOperation(n)}: ${w.setUpName}`}
                    value={op.name}
                    maxLength={200}
                    onChange={(e) => change(i, { name: e.target.value })}
                  />
                </td>
                <td>
                  {op.job_worker !== "" && op.work_centre === "" ? (
                    <span>{w.setUpJobWork}</span>
                  ) : (
                    <select
                      aria-label={`${w.setUpOperation(n)}: ${w.setUpWorkCentre}`}
                      value={op.work_centre}
                      onChange={(e) => change(i, { work_centre: e.target.value })}
                    >
                      <option value="">{w.setUpChoose}</option>
                      {preview.work_centres.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>
                  <input
                    aria-label={`${w.setUpOperation(n)}: ${w.setUpSetupMinutes}`}
                    inputMode="decimal"
                    value={op.setup_minutes}
                    onChange={(e) => change(i, { setup_minutes: e.target.value })}
                  />
                </td>
                <td>
                  <input
                    aria-label={`${w.setUpOperation(n)}: ${w.setUpCycleMinutes}`}
                    inputMode="decimal"
                    value={op.cycle_minutes}
                    onChange={(e) => change(i, { cycle_minutes: e.target.value })}
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => setOperations(operations.filter((_, at) => at !== i))}
                  >
                    {w.setUpRemoveOperation(n)}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button
        type="button"
        className="btn"
        onClick={() => {
          const last = numberIn(operations.at(-1)?.sequence ?? "") ?? 0;
          setOperations([
            ...operations,
            {
              sequence: String(last + 10),
              name: "",
              work_centre: "",
              job_worker: "",
              setup_minutes: "0",
              cycle_minutes: "0",
            },
          ]);
        }}
      >
        {w.setUpAddOperation}
      </button>
      <Unmatched unmatched={preview.unmatched} />
      <div className="acts">
        {canConfirm ? (
          <button type="submit" className="btn btn-go" disabled={busy || !ready}>
            {w.cardConfirm}
          </button>
        ) : null}
        {children}
      </div>
    </form>
  );
}

export function SetUpForm(props: SetUpFormProps) {
  const { preview } = props;
  return preview.kind === "bom" ? (
    <BomForm {...props} preview={preview} />
  ) : (
    <RoutingForm {...props} preview={preview} />
  );
}
